import { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ChatSidebar from './workspace/ChatSidebar';
import NotebookPane from './workspace/NotebookPane';
import FileExplorer from './workspace/FileExplorer';
import usePyodide from '../hooks/usePyodide';
import useTransformers from '../hooks/useTransformers';
import { useAuth } from '../hooks/useAuth';
import { Sparkles, GripVertical, PanelLeftClose, PanelLeft, Loader2, MessageSquare, X } from 'lucide-react';

export default function WorkspacePage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const pyodide = usePyodide();
  const transformers = useTransformers();
  const [activeFile, setActiveFile] = useState('main.py');
  const [sidebarWidth, setSidebarWidth] = useState(380);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const notebookRef = useRef(null);

  const getSidebarBounds = useCallback(() => {
    const minWidth = 260;
    const maxWidth = Math.max(minWidth, Math.floor(window.innerWidth * 0.5));
    return { minWidth, maxWidth };
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const onMouseMove = (ev) => {
      const { minWidth, maxWidth } = getSidebarBounds();
      const newWidth = Math.min(Math.max(startWidth + (ev.clientX - startX), minWidth), maxWidth);
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      setDragging(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [getSidebarBounds, sidebarWidth]);

  useEffect(() => {
    const onResize = () => {
      const { minWidth, maxWidth } = getSidebarBounds();
      setSidebarWidth((currentWidth) => Math.min(Math.max(currentWidth, minWidth), maxWidth));
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [getSidebarBounds]);

  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg" data-testid="workspace-auth-gate">
        <div className="text-center max-w-md mx-4">
          <Sparkles className="w-8 h-8 text-white mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl font-medium text-white mb-2">Sign in to access Workspace</h2>
          <p className="text-sm text-neutral-500 mb-6">Create a free account to start building with Codelves.</p>
          <button
            onClick={() => navigate('/?auth=register')}
            className="inline-flex items-center justify-center rounded-md bg-white text-black px-6 py-3 text-sm font-medium hover:bg-neutral-200 transition-colors"
            data-testid="workspace-signup-button"
          >
            Get Started Free
          </button>
          <p className="mt-3 text-xs text-neutral-600">
            Already have an account?{' '}
            <button onClick={() => navigate('/?auth=login')} className="text-neutral-400 hover:text-white underline underline-offset-2" data-testid="workspace-login-link">Sign in</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-bg text-white overflow-hidden" data-testid="workspace-page">
      <div className="flex items-center justify-between h-10 px-3 border-b border-white/[0.06] bg-[#070707] flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-1.5 text-neutral-400 hover:text-white transition-colors" data-testid="workspace-logo">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-heading text-xs font-medium">Codelves</span>
          </Link>
          <span className="text-[10px] text-neutral-600">|</span>
          <span className="text-[10px] text-neutral-500 font-mono">workspace</span>
          <span className="text-[10px] text-neutral-600">|</span>
          <Link
            to="/workshop"
            className="text-[10px] text-neutral-500 hover:text-white transition-colors font-mono border border-white/[0.08] px-2 py-0.5 rounded hover:border-white/20"
            data-testid="workspace-workshop-link"
          >
            Workshop
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile chat toggle — only visible on small screens */}
          <button
            onClick={() => setMobileChatOpen(!mobileChatOpen)}
            className="md:hidden text-neutral-500 hover:text-white transition-colors p-1"
            aria-label="Toggle chat"
            data-testid="toggle-mobile-chat"
          >
            {mobileChatOpen ? <X className="w-4 h-4" strokeWidth={1.5} /> : <MessageSquare className="w-4 h-4" strokeWidth={1.5} />}
          </button>
          {/* Desktop sidebar toggle — hidden on mobile */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex text-neutral-500 hover:text-white transition-colors p-1"
            data-testid="toggle-sidebar"
          >
            {sidebarCollapsed ? <PanelLeft className="w-4 h-4" strokeWidth={1.5} /> : <PanelLeftClose className="w-4 h-4" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile fullscreen chat overlay */}
        {mobileChatOpen && (
          <div className="md:hidden absolute inset-0 z-50 bg-[#080808] flex flex-col">
            <ChatSidebar
              runPython={pyodide.runPython}
              pyodideReady={pyodide.ready}
              transformers={transformers}
              notebookActions={notebookRef}
              activeFile={activeFile}
            />
          </div>
        )}

        {/* Desktop chat sidebar */}
        {!sidebarCollapsed && (
          <>
            <div style={{ width: sidebarWidth, minWidth: sidebarWidth }} className="hidden md:flex flex-shrink-0 border-r border-white/[0.06] overflow-hidden flex-col">
              <ChatSidebar
                runPython={pyodide.runPython}
                pyodideReady={pyodide.ready}
                transformers={transformers}
                notebookActions={notebookRef}
                activeFile={activeFile}
              />
            </div>

            <div
              onMouseDown={handleMouseDown}
              className={`hidden md:flex w-1 flex-shrink-0 cursor-col-resize items-center justify-center hover:bg-white/10 transition-colors ${
                dragging ? 'bg-white/10' : ''
              }`}
              data-testid="resize-handle"
            >
              <GripVertical className="w-3 h-3 text-neutral-700" strokeWidth={1} />
            </div>
          </>
        )}

        <div className="flex-1 flex overflow-hidden">
          <div className="w-48 flex-shrink-0 border-r border-white/[0.06] overflow-hidden">
            <FileExplorer activeFile={activeFile} onFileSelect={setActiveFile} />
          </div>

          <div className="flex-1 overflow-hidden">
            <NotebookPane
              ref={notebookRef}
              runPython={pyodide.runPython}
              pyodideReady={pyodide.ready}
              pyodideLoading={pyodide.loading}
              activeFile={activeFile}
              onFileSelect={setActiveFile}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
